import { fetcher } from "@certn/api";
import { components } from "@certn/api/generated-schemas/client-portal";
import { showToast } from "@certn/cascadia";

import { today, getLocalTimeZone, CalendarDate } from "@internationalized/date";

export function getInitials(name: string | null | undefined) {
  if (!name) {
    return "--";
  }

  const [firstName = " ", lastName = " "] = name.split(" ");
  return `${firstName[0]}${lastName[0]}`.toUpperCase();
}

export function buildCaseTabHref(
  basePath: string,
  tabPath: string,
  searchParams: URLSearchParams
) {
  const params = new URLSearchParams(searchParams);
  params.delete("page");
  params.delete("overall_status");
  params.delete("page_size");
  params.delete("ordering");
  params.delete("report_result");
  params.delete("client_decision");
  const queryString = params.toString();

  return `${basePath}${tabPath}${queryString ? `?${queryString}` : ""}`;
}

const CSV_DOWNLOAD_EXCLUDED_PARAMS = new Set(["page", "page_size", "ordering"]);

export function getCsvDownloadQueryParams(params: Record<string, string>) {
  return Object.fromEntries(
    Object.entries(params).filter(
      ([key, value]) => Boolean(value) && !CSV_DOWNLOAD_EXCLUDED_PARAMS.has(key)
    )
  );
}

export function hasCsvListDateFilter(params: Record<string, string>) {
  return Boolean(params.order_timeframe_after || params.order_timeframe_before);
}

/**
 * Returns the cases list base path from a tab pathname.
 * e.g. `/cases/review` -> `/cases`, `/group-overview/done` -> `/group-overview`
 */
export function getCasesBasePath(pathname: string) {
  const normalizedPath = pathname.split("?")[0].replace(/\/$/, "");

  if (normalizedPath.startsWith("/group-overview")) {
    return (
      normalizedPath.replace(/\/(review|done|legacy-hr|legacy-pm)$/, "") ||
      "/group-overview"
    );
  }

  if (normalizedPath.startsWith("/cases")) {
    return (
      normalizedPath.replace(/\/(review|done|legacy-hr|legacy-pm)$/, "") ||
      "/cases"
    );
  }

  return normalizedPath;
}

/**
 * Gets the first day of the current month as a CalendarDate
 */
export function getFirstDayOfMonth() {
  const now = today(getLocalTimeZone());
  return new CalendarDate(now.year, now.month, 1);
}

export function mapResultsToRows(
  results: components["schemas"]["BaseCaseList"][] | undefined
) {
  if (!results) {
    return [];
  }
  // The DataTable component (more specifically the Aria TableBody component) requires id
  return results.map((result) => ({ ...result, id: result.short_id }));
}

export function joinNameParts(
  name: components["schemas"]["ApplicantDetails"]["current_name"] | undefined
) {
  if (!name) return;

  return [name.given_name, name.additional_name, name.family_name]
    .filter((namePart) => Boolean(namePart))
    .join(" ");
}

export function download(
  preSignedUrl: string,
  documentName: string = "downloaded-file"
) {
  if (!preSignedUrl) {
    return false;
  }
  const link = document.createElement("a");
  link.href = preSignedUrl;
  link.target = "_blank";
  link.download = documentName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export async function getCertificateUrl(
  certificate:
    | {
        key: string | null;
        bucket: string | null;
      }
    | undefined
) {
  if (certificate?.key) {
    try {
      const response = await fetcher.request<{
        presigned_url: string;
      }>(
        `/api/internal/client-portal/checks/claim-files/download/${certificate.key}`
      );
      download(response.presigned_url, "uk-right-to-work-certificate");
    } catch (error) {
      console.error("Error downloading file", error);
      showToast("Something went wrong, try again", {
        type: "error",
      });
    }
  } else {
    showToast("Something went wrong, try again", {
      type: "error",
    });
  }
}

/**
 * Determines the case status subset from a pathname and base path.
 * Returns the status for active, review, or done tabs, or null for legacy tabs or invalid paths.
 *
 * @param pathname - The current pathname (may include query params)
 * @param basePath - The base path for the cases routes (e.g., "/cases" or "/group-overview")
 * @returns The status subset ("active" | "review" | "done") or null
 */
export function getStatusFromPath(
  pathname: string,
  basePath: string
): "active" | "review" | "done" | null {
  // Normalize pathname by removing query params and trailing slashes
  const normalizedPath = pathname.split("?")[0].replace(/\/$/, "");

  // Check for legacy tabs first (exclude these)
  if (
    normalizedPath === `${basePath}/legacy-hr` ||
    normalizedPath === `${basePath}/legacy-pm`
  ) {
    return null;
  }

  // Check for specific status routes (most specific first)
  if (normalizedPath === `${basePath}/review`) return "review";
  if (normalizedPath === `${basePath}/done`) return "done";
  if (normalizedPath === basePath) return "active";

  return null;
}
